import { useState, useEffect } from "react";
import api from "@/lib/api";
import Layout from "@/components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Shield, CheckCircle, XCircle, Trash2, Users, CreditCard, Lock, Eye, User, Phone, Mail, Building2, FileText, Database, Loader2 } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

// Função para formatar CPF
const formatCPF = (cpf) => {
  if (!cpf) return 'Não informado';
  const numbers = cpf.replace(/\D/g, '');
  if (numbers.length !== 11) return cpf;
  return numbers.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
};

// Função para formatar telefone
const formatPhone = (phone) => {
  if (!phone) return 'Não informado';
  const numbers = phone.replace(/\D/g, '');
  if (numbers.length === 11) {
    return numbers.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3');
  } else if (numbers.length === 10) {
    return numbers.replace(/(\d{2})(\d{4})(\d{4})/, '($1) $2-$3');
  }
  return phone;
};

export default function Admin({ onLogout }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteId, setDeleteId] = useState(null);
  const [subscriptionUser, setSubscriptionUser] = useState(null);
  const [selectedPlan, setSelectedPlan] = useState("");
  const [passwordUser, setPasswordUser] = useState(null);
  const [selectOpen, setSelectOpen] = useState(false);
  const [viewUser, setViewUser] = useState(null);
  const [importing, setImporting] = useState(false);

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await api.get('/admin/users');
      setUsers(response.data);
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error("Acesso negado: apenas administradores");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (userId) => {
    try {
      await api.post(`/admin/approve-user/${userId}`);
      toast.success("Usuário aprovado com sucesso!");
      fetchUsers();
    } catch (error) {
      toast.error("Erro ao aprovar usuário");
    }
  };

  const handleDelete = async () => {
    try {
      await api.delete(`/admin/delete-user/${deleteId}`);
      toast.success("Usuário deletado com sucesso!");
      fetchUsers();
      setDeleteId(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao deletar usuário");
    }
  };

  const handleRemovePassword = async (page) => {
    try {
      await api.delete(`/admin/remove-page-password/${passwordUser.id}/${page}`);
      toast.success(`Senha de ${page} removida!`);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao remover senha");
    }
  };

  const handleUpdateSubscription = async () => {
    if (!selectedPlan) {
      toast.error("Selecione um plano");
      return;
    }

    try {
      const months = selectedPlan === 'mensal' ? 1 : selectedPlan === 'semestral' ? 6 : 12;
      
      await api.post('/admin/update-subscription', {
        user_id: subscriptionUser.id,
        plan: selectedPlan,
        months: months
      });
      
      toast.success(`Assinatura ${selectedPlan} ativada com sucesso!`);
      fetchUsers();
      setSubscriptionUser(null);
      setSelectedPlan("");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao atualizar assinatura");
    }
  };

  const handleImportData = async () => {
    setImporting(true);
    try {
      const response = await api.post('/admin/import-data');
      toast.success(response.data.message || "Dados importados com sucesso!");
      fetchUsers(); // Recarregar usuários após importação
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao importar dados");
    } finally {
      setImporting(false);
    }
  };

  if (loading) {
    return <Layout onLogout={onLogout}><div className="text-center py-12">Carregando...</div></Layout>;
  }

  const pendingUsers = users.filter(u => !u.is_active && !u.is_admin);
  const activeUsers = users.filter(u => u.is_active || u.is_admin);

  return (
    <Layout onLogout={onLogout}>
      <div className="space-y-6">
        <div className="flex items-center gap-3">
          <Shield className="w-8 h-8 text-blue-600" />
          <div>
            <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold" style={{ fontFamily: 'Space Grotesk' }}>Painel Administrativo</h1>
            <p className="text-gray-600">Gerenciamento de usuários e permissões</p>
          </div>
        </div>

        {/* Cards de estatísticas */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card className="border-l-4 border-l-blue-500">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-gray-600 flex items-center gap-2">
                <Users className="w-4 h-4" />
                Total de Usuários
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-blue-600">{users.length}</div>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-l-green-500">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-gray-600 flex items-center gap-2">
                <CheckCircle className="w-4 h-4" />
                Usuários Ativos
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-green-600">{activeUsers.length}</div>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-l-orange-500">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-gray-600 flex items-center gap-2">
                <XCircle className="w-4 h-4" />
                Aguardando Aprovação
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-orange-600">{pendingUsers.length}</div>
            </CardContent>
          </Card>
        </div>

        {/* Card de Importação de Dados */}
        <Card className="border-purple-200 bg-purple-50">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-purple-800 flex items-center gap-2">
              <Database className="w-5 h-5" />
              Importar Dados do Backup
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-purple-700 mb-4">
              Clique no botão abaixo para importar os dados salvos (usuários, alunos, turmas, etc.) para o banco de dados atual.
            </p>
            <Button 
              onClick={handleImportData}
              disabled={importing}
              className="bg-purple-600 hover:bg-purple-700 w-full"
              data-testid="import-data-button"
            >
              {importing ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Importando...
                </>
              ) : (
                <>
                  <Database className="w-4 h-4 mr-2" />
                  Importar Dados Agora
                </>
              )}
            </Button>
          </CardContent>
        </Card>

        {/* Usuários pendentes */}
        {pendingUsers.length > 0 && (
          <Card className="border-orange-200 bg-orange-50">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-orange-800">
                <XCircle className="w-5 h-5" />
                Usuários Aguardando Aprovação
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {pendingUsers.map((user) => (
                  <div key={user.id} className="flex flex-col gap-3 sm:flex-row sm:justify-between sm:items-center p-3 sm:p-4 bg-white rounded-lg border">
                    <div className="flex-1 min-w-0">
                      <h4 className="font-semibold break-words">{user.username}</h4>
                      <p className="text-xs sm:text-sm text-gray-600">
                        Cadastrado em: {new Date(user.created_at).toLocaleDateString('pt-BR')}
                      </p>
                      {user.nome_escola && (
                        <p className="text-xs sm:text-sm text-blue-600 font-medium mt-1 break-words">
                          <Building2 className="w-3 h-3 inline mr-1" />
                          {user.nome_escola}
                        </p>
                      )}
                      {user.nome_completo && (
                        <p className="text-xs text-gray-500 mt-1 break-words">
                          <User className="w-3 h-3 inline mr-1" />
                          {user.nome_completo}
                        </p>
                      )}
                    </div>
                    <div className="flex gap-2 shrink-0 flex-wrap">
                      <Button
                        onClick={() => setViewUser(user)}
                        size="sm"
                        variant="outline"
                        className="text-purple-600 hover:bg-purple-50"
                        title="Ver Dados Cadastrais"
                      >
                        <Eye className="w-4 h-4" />
                      </Button>
                      <Button
                        onClick={() => handleApprove(user.id)}
                        size="sm"
                        className="bg-green-600 hover:bg-green-700"
                      >
                        <CheckCircle className="w-4 h-4 mr-2" />
                        Aprovar
                      </Button>
                      <Button
                        onClick={() => setDeleteId(user.id)}
                        size="sm"
                        variant="outline"
                        className="text-red-600 hover:bg-red-50"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Usuários ativos */}
        <Card>
          <CardHeader>
            <CardTitle>Usuários Ativos</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {activeUsers.map((user) => (
                <div 
                  key={user.id} 
                  className={`flex flex-col gap-3 sm:flex-row sm:justify-between sm:items-center p-3 sm:p-4 border rounded-lg ${
                    user.expiring_soon ? 'border-orange-500 border-2 bg-orange-50' : ''
                  }`}
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h4 className="font-semibold break-words">{user.username}</h4>
                      {user.is_admin && (
                        <Badge variant="destructive" className="bg-blue-600">
                          <Shield className="w-3 h-3 mr-1" />
                          Admin
                        </Badge>
                      )}
                      {!user.is_admin && user.subscription_plan && (
                        <Badge className="bg-green-600">
                          <CreditCard className="w-3 h-3 mr-1" />
                          {user.subscription_plan}
                        </Badge>
                      )}
                      {user.expiring_soon && (
                        <Badge className="bg-orange-600 animate-pulse">
                          ⚠️ Vence em {user.days_until_expiration} dia{user.days_until_expiration !== 1 ? 's' : ''}
                        </Badge>
                      )}
                    </div>
                    <p className="text-sm text-gray-600 mt-1">
                      {user.nome_escola || 'Gerenciador de Gestão de Escola Esportiva (GGEE)'}
                    </p>
                    <p className="text-xs text-gray-500">
                      Cadastrado em: {new Date(user.created_at).toLocaleDateString('pt-BR')}
                    </p>
                    {!user.is_admin && user.subscription_expires_at && (
                      <p className="text-xs text-gray-500">
                        Assinatura expira em: {new Date(user.subscription_expires_at).toLocaleDateString('pt-BR')}
                      </p>
                    )}
                    {!user.is_admin && (
                      <div className="flex gap-3 mt-2">
                        <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded">
                          ✓ {user.alunos_ativos || 0} ativos
                        </span>
                        <span className="text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded">
                          ✗ {user.alunos_inativos || 0} inativos
                        </span>
                        <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded font-semibold">
                          Total: {user.total_alunos || 0}
                        </span>
                      </div>
                    )}
                  </div>
                  {!user.is_admin && (
                    <div className="flex gap-2 shrink-0 flex-wrap">
                      <Button
                        onClick={() => setViewUser(user)}
                        size="sm"
                        variant="outline"
                        className="text-purple-600 hover:bg-purple-50"
                        title="Ver Dados Cadastrais"
                      >
                        <Eye className="w-4 h-4" />
                      </Button>
                      <Button
                        onClick={() => setSubscriptionUser(user)}
                        size="sm"
                        variant="outline"
                        className="text-green-600 hover:bg-green-50"
                        title="Gerenciar Assinatura"
                      >
                        <CreditCard className="w-4 h-4" />
                      </Button>
                      <Button
                        onClick={() => setPasswordUser(user)}
                        size="sm"
                        variant="outline"
                        className="text-blue-600 hover:bg-blue-50"
                        title="Remover Senhas de Páginas"
                      >
                        <Lock className="w-4 h-4" />
                      </Button>
                      <Button
                        onClick={() => setDeleteId(user.id)}
                        size="sm"
                        variant="outline"
                        className="text-red-600 hover:bg-red-50"
                        title="Deletar Usuário"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Dialog de Remoção de Senhas */}
      <Dialog open={!!passwordUser} onOpenChange={(open) => !open && setPasswordUser(null)}>
        <DialogContent
          onPointerDownOutside={(e) => e.preventDefault()}
          onInteractOutside={(e) => e.preventDefault()}
        >
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Lock className="w-5 h-5 text-blue-600" />
              Remover Senhas de Páginas
            </DialogTitle>
            <DialogDescription>
              Remover proteções de senha do usuário: <strong>{passwordUser?.username}</strong>
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-3">
            {passwordUser?.page_passwords && Object.keys(passwordUser.page_passwords).length > 0 ? (
              <>
                <p className="text-sm text-gray-600">Páginas protegidas com senha:</p>
                {Object.keys(passwordUser.page_passwords).map((page) => (
                  <div key={page} className="flex items-center justify-between p-3 border rounded-lg">
                    <div className="flex items-center gap-2">
                      <Lock className="w-4 h-4 text-orange-600" />
                      <span className="font-semibold capitalize">{page}</span>
                    </div>
                    <Button
                      onClick={() => handleRemovePassword(page)}
                      size="sm"
                      variant="destructive"
                    >
                      Remover
                    </Button>
                  </div>
                ))}
              </>
            ) : (
              <div className="text-center py-4 text-gray-500">
                <Lock className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                <p>Nenhuma página protegida com senha</p>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setPasswordUser(null)}
            >
              Fechar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog de Gerenciamento de Assinatura */}
      <Dialog 
        open={!!subscriptionUser} 
        onOpenChange={(open) => {
          // Só fecha se o Select não estiver aberto
          if (!open && !selectOpen) {
            setSubscriptionUser(null);
            setSelectedPlan("");
          }
        }}
      >
        <DialogContent
          onPointerDownOutside={(e) => e.preventDefault()}
          onInteractOutside={(e) => e.preventDefault()}
          onEscapeKeyDown={(e) => {
            // Não fechar com ESC se Select estiver aberto
            if (selectOpen) e.preventDefault();
          }}
        >
          <DialogHeader>
            <DialogTitle>Gerenciar Assinatura</DialogTitle>
            <DialogDescription>
              Ativar ou renovar assinatura para {subscriptionUser?.username}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium">Escolher Plano</label>
              <Select 
                value={selectedPlan} 
                onValueChange={setSelectedPlan}
                onOpenChange={setSelectOpen}
              >
                <SelectTrigger className="mt-2">
                  <SelectValue placeholder="Selecione um plano" />
                </SelectTrigger>
                <SelectContent position="popper" sideOffset={4}>
                  <SelectItem value="mensal">
                    <div className="flex flex-col">
                      <span className="font-semibold">Mensal - R$ 130,00/mês</span>
                      <span className="text-xs text-gray-500">Renovação a cada 30 dias</span>
                    </div>
                  </SelectItem>
                  <SelectItem value="semestral">
                    <div className="flex flex-col">
                      <span className="font-semibold">Semestral - R$ 110,00/mês</span>
                      <span className="text-xs text-gray-500">R$ 660,00 a cada 6 meses (15% desconto)</span>
                    </div>
                  </SelectItem>
                  <SelectItem value="anual">
                    <div className="flex flex-col">
                      <span className="font-semibold">Anual - R$ 90,00/mês</span>
                      <span className="text-xs text-gray-500">R$ 1.080,00 por ano (31% desconto)</span>
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            {subscriptionUser?.subscription_expires_at && (
              <div className="p-3 bg-blue-50 rounded-lg">
                <p className="text-sm text-blue-800">
                  <strong>Assinatura Atual:</strong> {subscriptionUser.subscription_plan}
                </p>
                <p className="text-sm text-blue-700">
                  Expira em: {new Date(subscriptionUser.subscription_expires_at).toLocaleDateString('pt-BR')}
                </p>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => { setSubscriptionUser(null); setSelectedPlan(""); setSelectOpen(false); }}>
              Cancelar
            </Button>
            <Button onClick={handleUpdateSubscription} className="bg-green-600 hover:bg-green-700">
              Ativar Assinatura
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteId} onOpenChange={(open) => !open && setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmar exclusão</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja excluir este usuário? Todos os dados associados (alunos, turmas, etc.) serão mantidos mas o usuário não poderá mais acessar.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Dialog de Visualização de Dados do Usuário */}
      <Dialog open={!!viewUser} onOpenChange={(open) => !open && setViewUser(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <User className="w-5 h-5 text-purple-600" />
              Dados Cadastrais
            </DialogTitle>
            <DialogDescription>
              Informações do usuário {viewUser?.username}
            </DialogDescription>
          </DialogHeader>
          
          {viewUser && (
            <div className="space-y-4">
              {/* Dados Pessoais */}
              <div className="space-y-3">
                <div className="flex items-center gap-2 text-gray-700 border-b pb-2">
                  <User className="w-4 h-4" />
                  <span className="text-sm font-semibold">Dados Pessoais</span>
                </div>
                
                <div className="grid grid-cols-1 gap-2 text-sm">
                  <div className="flex justify-between py-1 border-b border-gray-100">
                    <span className="text-gray-600">Nome Completo:</span>
                    <span className="font-medium">{viewUser.nome_completo || 'Não informado'}</span>
                  </div>
                  <div className="flex justify-between py-1 border-b border-gray-100">
                    <span className="text-gray-600 flex items-center gap-1">
                      <FileText className="w-3 h-3" /> CPF:
                    </span>
                    <span className="font-medium">{formatCPF(viewUser.cpf)}</span>
                  </div>
                  <div className="flex justify-between py-1 border-b border-gray-100">
                    <span className="text-gray-600 flex items-center gap-1">
                      <Phone className="w-3 h-3" /> Telefone:
                    </span>
                    <span className="font-medium">{formatPhone(viewUser.telefone)}</span>
                  </div>
                  <div className="flex justify-between py-1 border-b border-gray-100">
                    <span className="text-gray-600 flex items-center gap-1">
                      <Mail className="w-3 h-3" /> Email:
                    </span>
                    <span className="font-medium text-blue-600">{viewUser.email || 'Não informado'}</span>
                  </div>
                </div>
              </div>

              {/* Dados da Escola */}
              <div className="space-y-3">
                <div className="flex items-center gap-2 text-gray-700 border-b pb-2">
                  <Building2 className="w-4 h-4" />
                  <span className="text-sm font-semibold">Dados da Escola</span>
                </div>
                
                <div className="bg-blue-50 p-3 rounded-lg">
                  <span className="font-medium text-blue-800">{viewUser.nome_escola || 'GGEE'}</span>
                </div>
              </div>

              {/* Dados da Conta */}
              <div className="space-y-3">
                <div className="flex items-center gap-2 text-gray-700 border-b pb-2">
                  <CreditCard className="w-4 h-4" />
                  <span className="text-sm font-semibold">Dados da Conta</span>
                </div>
                
                <div className="grid grid-cols-1 gap-2 text-sm">
                  <div className="flex justify-between py-1 border-b border-gray-100">
                    <span className="text-gray-600">Usuário:</span>
                    <span className="font-medium">{viewUser.username}</span>
                  </div>
                  <div className="flex justify-between py-1 border-b border-gray-100">
                    <span className="text-gray-600">Cadastrado em:</span>
                    <span className="font-medium">{new Date(viewUser.created_at).toLocaleDateString('pt-BR')}</span>
                  </div>
                  <div className="flex justify-between py-1 border-b border-gray-100">
                    <span className="text-gray-600">Plano:</span>
                    <Badge className={viewUser.subscription_plan ? 'bg-green-600' : 'bg-gray-400'}>
                      {viewUser.subscription_plan || 'Sem plano'}
                    </Badge>
                  </div>
                  {viewUser.subscription_expires_at && (
                    <div className="flex justify-between py-1 border-b border-gray-100">
                      <span className="text-gray-600">Expira em:</span>
                      <span className="font-medium">{new Date(viewUser.subscription_expires_at).toLocaleDateString('pt-BR')}</span>
                    </div>
                  )}
                  <div className="flex justify-between py-1">
                    <span className="text-gray-600">Total de Alunos:</span>
                    <span className="font-medium">{viewUser.total_alunos || 0}</span>
                  </div>
                </div>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setViewUser(null)}>
              Fechar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
